import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import type { Fact } from '@/domains/types'
import { resolveLanguage } from '@/application/i18n/language'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { hasContent } from './hasContent'

/**
 * A single fact rendered as an article paragraph: title, description and — if
 * present — a plain inline source link. Deliberately does NOT reuse
 * `LeadFactsSection`'s bordered-pill "Source" button styling: that's a UI
 * control for the management/moderation view, whereas read mode wants a
 * citation that reads like a footnote in running text.
 */
export function ReadFactBlock({ fact }: { fact: Fact }) {
  const { t, i18n } = useTranslation()
  // The backend resolves `fact.url` through the same fallback chain as
  // title/description (TRANSLATION-PLAN.md §8): when the UI locale differs
  // from the fact's own source locale, the URL we got back may be a
  // translated link OR a fallback to the untranslated source link. There is
  // no separate flag for "this is a fallback", so — mirroring the plan's
  // affordance for source links — mark `lang` with the fact's source locale
  // whenever the UI isn't already reading in that locale; a translated link
  // still gets a (harmless) `lang` hint, an untranslated one gets a correct
  // one, and a screen reader never mispronounces it as UI-locale text.
  const uiLocale = resolveLanguage(i18n.language)
  const sourceLocale = resolveLanguage(fact.sourceLocale ?? 'fr')
  const linkLang = uiLocale !== sourceLocale ? sourceLocale : undefined

  return (
    <div className="read-fact-block mb-6 last:mb-0">
      {/* `mb-6` mirrors the block's own `mb-6`, i.e. the gap above this
          title, so a fact heading is spaced identically top and bottom. */}
      <h3 className="text-2xl font-display font-semibold text-foreground mb-6">{fact.title}</h3>
      {fact.imageUrl && (
        <figure className="mb-6">
          {/* `aspect-[21/9]` reserves the image's box before it loads, so a
              TOC anchor landing below it isn't thrown off by layout shift
              once the image arrives. */}
          <div className="rounded-lg overflow-hidden aspect-[21/9]">
            <img src={fact.imageUrl} alt={fact.title} className="w-full h-full object-cover" />
          </div>
        </figure>
      )}
      {hasContent(fact.description) && (
        <MarkdownView content={fact.description} variant="article" />
      )}
      {fact.url && (
        <a
          href={fact.url}
          target="_blank"
          rel="noopener noreferrer"
          lang={linkLang}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline text-wrap-anywhere"
        >
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          {t('lead_detail.fact_source_button')}
        </a>
      )}
    </div>
  )
}
